import performSearchFilterAsync from '@Containers/ResultsFilters/performSearchFilterAsync';
import { preparePropertiesForRequest } from '@Containers/ResultsFilters/performSearchFilterAsync/preparePropertiesUtils';
import NewRelicService, { BROWSER_INTERACTIONS_NAMES } from '@Services/NewRelicService';
import {
  filters as filtersDataSelector,
  keyifyParamsForFiltersRequest,
  paramsForFiltersRequest as paramsForFiltersRequestSelector,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';
import {
  query as queryTextSelector,
  previewResults as previewResultsSelector,
} from '@Pages/QuickSearch/Components/QuickResults/QuickResultsSelectors';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { clearFilterState } from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';
import AppStorage from '@Services/AppStorage';
import assignIn from 'lodash/assignIn';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';
import { setAppliedFilterSummary } from '@Containers/ResultsFilters/ResultsFiltersActions';

const getSelectedCount = (filter) => {
  const type = filter.get('type');

  if (type === 1 || type === 2) {
    const selectedGroups =
      filter
        .get('groups')
        ?.filter((group) => group.get('selected'))
        .count() || 0;
    return selectedGroups + (filter.get('userInputValue') ? 1 : 0);
  }
  if (type === 4 || type === 5) {
    return filter.get('selected') ? 1 : 0;
  }
  if (type === 6) {
    return filter.get('userInputValue')?.trim() ? 1 : 0;
  }
  return 0;
};

export const getAppliedFilterCategories = (filters) =>
  (filters || []).reduce((categories, filter) => {
    const count = getSelectedCount(filter);
    if (!count) {
      return categories;
    }

    const sanitizedTitle = sanitizeDiscard(filter.get('title') || 'Filter', { allowedTags: [] });
    const label = filter.get('type') === 5 ? 'Drawn structure' : sanitizedTitle || 'Filter';
    const existingIndex = categories.findIndex((category) => category.label === label);
    if (existingIndex === -1) {
      return categories.concat({ label, count });
    }

    return categories.map((category, index) => (index === existingIndex ? { ...category, count: category.count + count } : category));
  }, []);

const storeAppliedFilterSummary = (dispatch, filterResult, filters, action) => {
  if (!filterResult?.hasResults) {
    return;
  }

  const { searchFilterResult } = filterResult;
  dispatch(
    setAppliedFilterSummary({
      action,
      categories: getAppliedFilterCategories(filters),
      resultCount: searchFilterResult.resultSize,
      resultName: searchFilterResult.resultName,
    })
  );
};

const getSemanticSearchValues = (state) => {
  const previewResults = previewResultsSelector(state) || [];
  const semanticCitationsResult = previewResults.find((item) => item.context === 'citations' && item.semanticSearchIncluded === true);

  if (!semanticCitationsResult) {
    return {};
  }

  return {
    textQuery: queryTextSelector(state),
    semanticSearchIncluded: semanticCitationsResult.semanticSearchIncluded,
    semanticResultName: semanticCitationsResult.semanticResultName,
  };
};

const runLimitTo = (baseParamsExtensionOverride, propertiesOverride, filterOptions) => async (dispatch, getState) => {
  try {
    const state = getState();
    const params = paramsForFiltersRequestSelector(state);
    let browserInteraction;
    if (params.context === 'substances') {
      browserInteraction = NewRelicService.createBrowserInteraction(BROWSER_INTERACTIONS_NAMES.TIME_TO_APPLY_FILTERS_TO_SUBSTANCES);
    }

    let baseParamsExtension;
    // retrieve semantic search values from quick results state
    if (params.context === 'citations') {
      baseParamsExtension = getSemanticSearchValues(state);
      Logger.info('Applying semantic search values to citations filter request (limitTo)', baseParamsExtension);
    }

    if (baseParamsExtensionOverride) {
      baseParamsExtension = {
        ...baseParamsExtension,
        ...baseParamsExtensionOverride,
      };
    }

    const filters = filtersDataSelector(state);
    const properties = propertiesOverride || preparePropertiesForRequest(filters, state);

    const filterResult = await performSearchFilterAsync({ filters, properties, browserInteraction, baseParamsExtension, ...filterOptions });

    storeAppliedFilterSummary(dispatch, filterResult, filters, 'LIMIT');

    AppStorage.instance.setToSessionStorage('integratedFeedbackHide', true);

    return filterResult;
  } catch (e) {
    Logger.error('filters on react: limit to exception', e);
    return null;
  }
};

export const limitTo = withGlobalSpinnerActionEnhancer(() => runLimitTo());

export const limitToWithBaseParamsExtension = withGlobalSpinnerActionEnhancer((baseParamsExtension) => runLimitTo(baseParamsExtension));

export const limitToWithPreferenceProperties = withGlobalSpinnerActionEnhancer((preferenceProperties, baseParamsExtension, filterOptions) =>
  runLimitTo(baseParamsExtension, preferenceProperties, filterOptions)
);

export const exclude = withGlobalSpinnerActionEnhancer(() => async (dispatch, getState) => {
  try {
    const state = getState();
    const filters = filtersDataSelector(state);
    const params = paramsForFiltersRequestSelector(state);
    const properties = preparePropertiesForRequest(filters, state);

    const baseParamsExtension = { exclude: true };
    // retrieve semantic search values from quick results state
    if (params.context === 'citations') {
      assignIn(baseParamsExtension, getSemanticSearchValues(state));
      Logger.info('Applying semantic search values to citations filter request (Exclude)', baseParamsExtension);
    }

    const filterResult = await performSearchFilterAsync({
      filters,
      properties,
      baseParamsExtension,
    });

    storeAppliedFilterSummary(dispatch, filterResult, filters, 'EXCLUDE');

    AppStorage.instance.setToSessionStorage('integratedFeedbackHide', true);
    return filterResult;
  } catch (e) {
    Logger.error('filters on react: exclude exception', e);
    return null;
  }
});

export const clear = withGlobalSpinnerActionEnhancer(() => async (dispatch, getState) => {
  try {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);

    const action = clearFilterState(key);
    dispatch(action);
    return true;
  } catch (e) {
    Logger.error('filters on react: clear exception', e);
    return false;
  }
});
